// Adrigo Insights AI — standardized platform playbook.
// Single source of truth used by BOTH the public /resources page and the
// authenticated /dashboard/help page so copy stays in lockstep.
//
// COPY GUIDELINE: keep this benefit-driven and plain-English for non-technical
// corporate clients and marketing teams. No DB jargon, no model names, no SDKs.
import {
  CreditCard, Settings2, Gift, ShieldCheck, Activity,
} from "lucide-react";

export const KB_CHAPTERS = [
  {
    id: "setup-billing",
    title: "Setup & Billing",
    icon: CreditCard,
    summary:
      "Get your team set up in minutes, fund your workspace through Stripe Checkout, and always know exactly what you're paying for.",
    sections: [
      {
        h: "1. Create your brand workspace",
        body: [
          "Sign up with your work email and a few details about your company. Each brand gets its own private workspace — campaigns, respondents, payouts, and reports are never shared with other clients.",
          "Invite teammates so they can view live results, observe interviews in real time, or download the final report. Permissions are role-based, so only authorized users can launch or pay for new studies.",
        ],
      },
      {
        h: "2. Fund campaigns through Stripe Checkout",
        body: [
          "When you confirm a study on the Review & Pay step, we hand you off to Stripe's secure hosted checkout. You pay by card the same way you'd buy anything online — Adrigo never sees or stores your card details.",
          "The total on the Invoice Summary is what you'll be charged. There are no surprise fees, no hidden minimums, and no add-ons applied after the fact.",
        ],
      },
      {
        h: "3. Transparent invoicing",
        body: [
          "Every campaign quote breaks the cost into clear lines: participant incentives, platform service fee, and (if turned on) the video interview add-on. You see the math before you pay.",
          "All campaigns are billed in CAD; Stripe handles foreign-currency conversion automatically. Canadian tax (HST/GST) is calculated for billing addresses that require it. A printable receipt is emailed to you the moment payment clears.",
        ],
      },
      {
        h: "4. Refunds and unused budget",
        body: [
          "If you stop a campaign before it reaches its target, any unused incentive budget is refunded to your card through Stripe — typically within 5–7 business days. The platform service fee for completed interviews is non-refundable.",
        ],
      },
    ],
  },
  {
    id: "campaign-config",
    title: "Campaign Configuration",
    icon: Settings2,
    summary:
      "Tell the AI interviewer how to behave, decide how many responses you want, and set a few simple guardrails — no scripting or branching logic required.",
    sections: [
      {
        h: "1. Set your target number of responses",
        body: [
          "Pick how many completed interviews you need — anywhere from 10 for a quick read to several thousand for statistically robust segmentation. The platform automatically closes the study the moment your target is reached, so you never pay for more responses than you asked for.",
          "Choose where respondents come from: our internal panel, the global programmatic exchange, your own customer list, or a blend of all three. Defaults are tuned to fill the fastest, but you can adjust the mix per study.",
        ],
      },
      {
        h: "2. Write the questions you want answered",
        body: [
          "Add 3 open-ended questions about the topic you're researching. The AI interviewer asks each one in a natural, conversational way and then probes for the 'why behind the why' — exactly like a senior moderator would, but at thousands of conversations in parallel.",
          "Optionally add up to 3 quick screener questions at the start of the study to filter respondents in or out (e.g., 'Do you shop with us at least once a month?'). Anyone who doesn't qualify is politely thanked and exited without spending budget.",
        ],
      },
      {
        h: "3. Shape the AI interviewer's tone",
        body: [
          "Tell the platform how you want the interviewer to feel — warm and curious, fast and to-the-point, or professional and formal. The system follows your instructions in every conversation, so the tone matches your brand.",
          "Built-in guardrails make sure the interviewer never leads, agrees, or sympathizes with an opinion — it only ever probes for richer detail. Your data stays neutral and unbiased by default.",
        ],
      },
      {
        h: "4. Add video for richer signals (optional)",
        body: [
          "Flip the Video toggle to capture respondents' webcam alongside their audio at a flat $12 CAD per complete. Useful when facial cues and reactions matter — e.g., product reveals, ad concept testing, packaging studies. Audio-only stays the default and incurs no upcharge.",
        ],
      },
    ],
  },
  {
    id: "incentive-delivery",
    title: "Dual-Mode Incentive Delivery",
    icon: Gift,
    summary:
      "Pay respondents instantly when you need to attract a new audience, or skip incentives entirely when you're surveying your own customers.",
    sections: [
      {
        h: "1. Incentivized Campaigns — automated rewards",
        body: [
          "The default mode when you need to recruit respondents. Our Programmatic Panel routes pre-vetted external participants into your study, and pays them automatically the moment Adrigo approves their assignment. No gift-card emails — payouts settle inside the panel's wallet.",
          "You don't manage anything manually. No spreadsheets, no chasing payouts, no fraud risk. Every reward shows up in your dashboard with its delivery status so you can confirm at a glance that respondents have been paid.",
          "Per-response cost is your chosen incentive amount plus a 1.5× platform service fee that funds the AI moderator, fraud guardrails, transcription, emotion analysis, and the final report.",
        ],
      },
      {
        h: "2. Owned-List Campaigns — your own database, no rewards",
        body: [
          "Untick 'Enable Participant Rewards' when you're surveying people who already know your brand — your existing customers, your NPS list, your employee panel, your beta community. Adrigo skips the gift-card step entirely.",
          "Respondents see a clean, branded thank-you screen instead of a reward link. You still get the same rich interviews, the same emotion analysis, and the same executive deliverable — without the cost of incentives.",
          "Pricing drops to just the platform service fee per completed response. Transparent, flat, predictable — perfect for ongoing internal voice-of-customer work.",
        ],
      },
      {
        h: "3. Switching modes mid-program",
        body: [
          "Different studies in the same workspace can run in different modes — incentivized to acquire fresh perspective from strangers, owned-list to deepen what you already know about your customers. Mix and match study-by-study; the platform handles both seamlessly.",
        ],
      },
    ],
  },
  {
    id: "data-integrity",
    title: "Data Integrity Matrix",
    icon: ShieldCheck,
    summary:
      "Built-in fraud protection that blocks duplicate respondents, protects your budget, and ensures every insight is based on a clean dataset.",
    sections: [
      {
        h: "1. One-and-Done protection",
        body: [
          "Every study automatically enforces a 'one response per person, per campaign' rule. Once someone has participated in a campaign, any attempt to come back and re-enter — on the same device, a new browser, or a fresh email — is politely rejected with a clear message: 'You have already participated in this research campaign.'",
          "You never pay twice for the same person. Your incentive budget stretches further. Your results stay statistically sound.",
        ],
      },
      {
        h: "2. Works across every recruitment source",
        body: [
          "The same protection runs whether respondents come from our internal panel, the programmatic exchange, or your own intercept link — uniformly, automatically, no setup required from you.",
          "Internal panelists are matched by their account; exchange respondents are matched by the global ID their network supplies; intercept visitors are matched by a privacy-safe browser signature. Three different sources, one consistent rule.",
        ],
      },
      {
        h: "3. Real-time response quality scoring",
        body: [
          "Every conversation is monitored as it happens. The platform flags tell-tale signs of low-effort or fraudulent responses — extremely short answers, copy-paste content, tab-switching during the interview, gibberish, robotic timing patterns. Suspicious sessions are stopped before payout, protecting both your budget and your data quality.",
        ],
      },
      {
        h: "4. Privacy by design",
        body: [
          "Adrigo is PIPEDA-aligned by default. We never store raw personal identifiers like IP addresses or browser strings — only privacy-safe signatures scoped to a single campaign. When a campaign is deleted, every trace of its respondents is deleted with it. Your participants stay private; your insights stay clean.",
        ],
      },
    ],
  },
  {
    id: "audio-analytics",
    title: "Acoustic Sentiment & Tone Mapping",
    icon: Activity,
    summary:
      "Go beyond what respondents say — measure how they say it. Vocal energy, pitch shifts, hesitation, and pacing reveal the genuine emotion behind every answer.",
    sections: [
      {
        h: "1. What we listen for",
        body: [
          "Whenever respondents speak, the platform analyzes the sound of their voice in addition to their words. We track four signals that great researchers listen for instinctively: energy (how loud and emphatic), pitch variation (how animated), pacing (how confident and fluent), and hesitation (the pauses and stutters that hint at uncertainty).",
          "Text alone misses these cues. Acoustic Sentiment captures them automatically, at scale, on every interview.",
        ],
      },
      {
        h: "2. Three scores that summarize the moment",
        body: [
          "Each answer gets three simple 0–100 scores:",
          "Engagement — how emotionally activated the respondent sounded. Higher means excited, animated, or fired-up; lower means flat, tired, or disengaged.",
          "Positivity — how warm and upbeat the delivery felt. Higher leans positive and approving; lower leans frustrated or negative.",
          "Confidence — how certain and fluent the respondent sounded. Higher means decisive; lower means hesitant or unsure.",
          "Each moment also gets a plain-English emotion label: excited, frustrated, calm, subdued, or neutral.",
        ],
      },
      {
        h: "3. Read the emotion timeline at a glance",
        body: [
          "On every campaign's executive report you'll see three headline scores for the whole study, an emotion mix breakdown, and a timeline showing how engagement and positivity rose or fell across each question. Hover any bar to read the exact verbatim quote behind that emotional spike — the moment that mattered, in the respondent's own words.",
          "It's the difference between knowing 'people liked the packaging' and knowing 'people lit up when they saw the packaging — engagement jumped 28 points compared to the rest of the interview.'",
        ],
      },
      {
        h: "4. Ask questions of the emotional data",
        body: [
          "Chat with the built-in Insight Agent on any campaign — it pulls the most relevant respondent quotes AND cites the emotional shifts behind them. Ask things like 'where were respondents most frustrated?' or 'what topic made people most excited?' and get answers grounded in both what they said and how they said it.",
        ],
      },
    ],
  },
];

// 3-step landing teaser — concise, click-bait-y, links to the full /resources page
export const PLATFORM_WALKTHROUGH = [
  {
    n: "01",
    title: "Configure",
    body:
      "Pick who you want to hear from, write 3 open-ended questions, set a target number of responses, and choose whether to pay incentives or survey your own list. One transparent Stripe Checkout — no surprises.",
  },
  {
    n: "02",
    title: "Collect",
    body:
      "Voice and (optionally) video respondents flow in from your chosen sources. The AI interviewer asks follow-ups in real time, just like a great moderator. Built-in fraud guards keep the dataset clean.",
  },
  {
    n: "03",
    title: "Decide",
    body:
      "Read the emotional tone behind every answer, browse top themes and verbatim quotes, and chat with the data through the Insight Agent. Respondents get paid instantly. You walk out with a boardroom-ready report in days, not weeks.",
  },
];
